import { Component, OnInit, ViewChild, AfterViewInit, TemplateRef, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ServerApiService } from '../../../server-api/server-api.service';
import { AppContextService } from '../../../services/app-context.service';
import { TranslateService } from '../../../services/translate/translate.service';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';
import { HttpErrorResponse } from '@angular/common/http';
import { OperationResult } from '../../../models/server-result/operation-result';
import { Document } from '../../../models/document/document';
import { DocumentSearchCriteria } from '../../../models/document/document-search-criteria';
import { CodeList } from '../../../models/common/code-list';
import { User } from '../../../models/User/user';
import { DocumentPaymentState } from '../../../models/enums/document-payment-state';
import { Report } from '../../../models/reports/Report';
import { UtilitiesService } from '../../../services/utils/utilities.service';
import { LogService } from '../../../services/log/log.service';
import { OperationError } from '../../../models/server-result/operation-error';
import { FileIntegration } from '../../../models/file-integrations/file-integration';
import { QueryStringDocumentSearchCriteria } from './models/query-string-document-search-criteria';
import dayjs from '../../../services/dayjs-setup';
import { DocumentInvolvement } from '../../../models/document/document-involvement';
import { DocumentViewModel } from './models/document-view-model';
import { SearchDocumentInvolvementInput } from '../../../models/document/search-document-involvement-input';
import { DocumentInvolvedEntityType } from '../../../models/enums/confirming/document-involvement-entity-type';
import { PublicCompany } from '../../../models/companies/public-company';
import { finalize, forkJoin, Observable } from 'rxjs';
import { ExecutionPermissionValidatorService } from '../../../services/validations/execution-permission-validator.service';
import { DocumentUploadContractType, DocumentUploadContractTypeExtensions } from '../../../models/enums/document-upload-contract-type';
import { FileIntegrationApiService } from '../../../server-api/file-integration-api.service';
import {
	TableColumn,
	TableExportOption,
	TableFilter,
	TablePagination,
	TableSearchEvent,
	TableSelectionEvent,
} from '../../../shared-components/custom-table/custom-table.types';
import { TableSelectAction } from '../../../shared-components/custom-table/components/table-toolbar/table-toolbar.component';
import { CustomTableComponent } from '../../../shared-components/custom-table/custom-table.component';
import { CurrencyLocalePipe } from '../../../services/currency-locale.pipe';
import { CurrencyPipe } from '@angular/common';
import { DateLocalePipe } from '../../../services/date-locale.pipe';
import { html } from '../../../utils/html-tag-utils';
import { BreadcrumbItem } from '../../../shared-components/custom-breadcrumbs/custom-breadcrumbs.component';
import { BaseFilterableComponent } from '../../../shared-components/custom-table/base-filterable-component';
import { FilterMappingConfig } from '../../../shared-components/custom-table/custom-table-filter-mapper.service';
import { CustomTableFilterMapperService } from '../../../shared-components/custom-table/custom-table-filter-mapper.service';
import { ContractsInCompanyValidation } from '../../../shared-components/contracts-in-company/contracts-in-company.component';
import { ReportingApiService } from '../../../server-api/reporting-api.service';
import { ReportingFinancialMovementsCriteria } from '../../../models/financial-movement/financial-movement';
import { ReportingExportHelperService } from '../../../services/reporting-exports/reporting-export-helper.service';
import { MenuItem } from '../../../shared-components/custom-menu/custom-menu.component';
import { CustomDatePickerComponent } from '../../../shared-components/custom-date-picker/custom-date-picker.component';
import { ValidationRule } from '../../../models/validation-rules/validation-rules';
import { CustomModalService } from '../../../shared-components/custom-modal/custom-modal.service';
import { CodeListApiService } from '../../../server-api/code-list-api/code-list-api.service';
import {
	ApprovalStateEnum,
	DocumentAdvanceStatesEnum,
	DocumentTypesEnum,
	PaymentStatesEnum,
} from '../../../server-api/code-list-api/code-list-api.enums';

@Component({
	selector: 'app-search-documents',
	templateUrl: './search-documents.component.html',
	styleUrls: ['./search-documents.component.scss'],
	standalone: false,
})
export class SearchDocumentsComponent extends BaseFilterableComponent implements OnInit, AfterViewInit {
	@ViewChild('customTable') customTable: CustomTableComponent;
	@ViewChild('voidDocumentsButton') voidDocumentsButton: TemplateRef<any>;

	public documents: DocumentViewModel[] = [];
	public DocumentSearchCriteria: DocumentSearchCriteria = new DocumentSearchCriteria();
	public requestErrors: OperationError[] = [];

	public docTypes: string[] = [];
	public approvalStates: string[] = [];
	public documentAdvancedStates: string[] = [];
	public paymentStates: string[] = [];
	public suppliers: PublicCompany[] = [];
	public buyers: PublicCompany[] = [];

	public isCollapsed: boolean;
	public isResourceLoaded: boolean;
	public isPerformingRequest: boolean = false;
	public isUploadDropdownOpen: boolean = false;

	public loggedUser: User | null = null;

	private fileId: string | null = null;
	public fileIntegrationDetail: FileIntegration | null = null;
	private queryStringDocumentSearchCriteria: QueryStringDocumentSearchCriteria | null = null;

	public breadcrumbItems: BreadcrumbItem[] = [];
	public isTableReady = false;
	public isTableLoading = true;
	public selectActions: TableSelectAction[] = [];
	public selectedRows: DocumentViewModel[] = [];
	public isCardView = false;
	public tableFilters: TableFilter[] = [];
	public tablePagination: TablePagination = {
		pageIndex: 0,
		pageSize: 25,
		pageSizeOptions: [],
		totalItems: 0,
	};

	public isModalVisible = false;
	public selectedDate: any = null;
	public modalDateRange: string[] = [];
	public isDateRangeValid: boolean = false;

	public uploadContractType: DocumentUploadContractType | null = null;
	public companyId: string = '';
	public fileRelatedContractId: string = '';
	public contractsInCompanyValidation: ContractsInCompanyValidation | null = null;
	public isExportingRequest: boolean = false;

	protected filterMappings: FilterMappingConfig[] = [
		{
			field: 'Type',
			targetProperty: 'TypeList',
			type: 'array',
		},
		{
			field: 'Number',
			targetProperty: 'Number',
			type: 'string',
		},
		{
			field: 'Supplier',
			targetProperty: 'SupplierIdList',
			type: 'array',
		},
		{
			field: 'Buyer',
			targetProperty: 'BuyerIdList',
			type: 'array',
		},
		{
			field: 'BuyerPaymentDate',
			targetProperty: 'BuyerPaymentDateStart|BuyerPaymentDateEnd',
			type: 'date-range',
		},
		{
			field: 'CreationDate',
			targetProperty: 'CreationDateStart|CreationDateEnd',
			type: 'date-range',
		},
		{
			field: 'ApprovalState',
			targetProperty: 'ApprovalStateList',
			type: 'array',
		},
		{
			field: 'PaymentState',
			targetProperty: 'PaymentStateList',
			type: 'array',
		},
	];

	protected searchCriteria = this.DocumentSearchCriteria;

	constructor(
		private codeListApiService: CodeListApiService,
		private serverApiService: ServerApiService,
		private fileIntegrationApiService: FileIntegrationApiService,
		public appContext: AppContextService,
		private translateService: TranslateService,
		private toasterService: ToasterService,
		private route: ActivatedRoute,
		private modalService: CustomModalService,
		private router: Router,
		private logger: LogService,
		private utilitiesService: UtilitiesService,
		private permissionValidatorService: ExecutionPermissionValidatorService,
		private cdr: ChangeDetectorRef,
		protected filterMapper: CustomTableFilterMapperService,
		private reportingApiService: ReportingApiService,
		private reportingExportHelperService: ReportingExportHelperService
	) {
		super();
	}

	ngOnInit() {
		this.setFilterMapper(this.filterMapper);

		this.uploadContractType = DocumentUploadContractType.Confirming;

		this.initializeBreadcrumbs();
		this.initializeTableFilters();

		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;
			this.loadListsForFilters();
			this.route.params.subscribe((params) => {
				const fileId: string = params['fileId'];
				this.fileId = fileId;
				if (!!this.fileId) this.GetEtlFileDetails(this.fileId);
				const searchCriteria: string = params['searchCriteria'];
				if (!!searchCriteria) this.ProcessSearchCriteria(searchCriteria);
			});
		});
	}

	ngAfterViewInit() {
		this.selectActions = [
			{
				template: this.voidDocumentsButton,
				disabled: false,
			},
		];
		this.cdr.detectChanges();
	}

	public tableColumns: TableColumn[] = [
		{
			field: 'Number',
			header: 'search-docs.doc',
			render: (row: DocumentViewModel) => {
				return html`
					<p>${this.translateService.translate(row.Type, 'list-docType')}</p>
					<span class="is-bold">${row.Number}</span>
				`;
			},
			tooltip: (row: DocumentViewModel) => `${this.translateService.translate(row.Type, 'list-docType')} ${row.Number}`,
		},
		{
			field: 'Supplier',
			header: 'search-docs.supplier',
			render: (row: DocumentViewModel) => {
				return html`
					<p class="truncate">${row.Supplier?.Name}</p>
					<span>${row.Supplier?.CountryCode}${row.Supplier?.VatNumber}</span>
				`;
			},
			tooltip: (row: DocumentViewModel) => `${row.Supplier?.Name} ${row.Supplier?.CountryCode}${row.Supplier?.VatNumber}`,
		},
		{
			field: 'Buyer',
			header: 'search-docs.buyer',
			render: (row: DocumentViewModel) => {
				return html`
					<p class="truncate">${row.Buyer?.Name}</p>
					<span>${row.Buyer?.CountryCode}${row.Buyer?.VatNumber}</span>
				`;
			},
			tooltip: (row: DocumentViewModel) => `${row.Buyer?.Name} ${row.Buyer?.CountryCode}${row.Buyer?.VatNumber}`,
		},
		{
			field: 'TotalAmount',
			header: 'search-docs.docAmount',
			align: 'right',
			render: (row: DocumentViewModel) => {
				const amount = row.TotalAmount || 0;
				const currency = row.Currency || '';
				const currencyPipe = new CurrencyLocalePipe(new CurrencyPipe(this.appContext.getCurrentLanguage()));
				return html`<span class="is-bold">${currencyPipe.transform(amount, currency)}</span>`;
			},
		},
		{
			field: 'CreationDate',
			header: 'search-docs.creationDate',
			render: (row: DocumentViewModel) => {
				return html`<span>${row.CreationDate}</span>`;
			},
		},
		{
			field: 'BuyerPaymentDate',
			header: 'search-docs.confirmedPaymentDate',
			disclaimer: {
				icon: 'fa-solid fa-exclamation-circle',
				color: 'danger',
				message: this.translateService.translate('search-docs.confirmedPaymentDate.tooltip', 'client'),
			},
			render: (row: DocumentViewModel) => {
				const datePipe = new DateLocalePipe(this.appContext);
				return html`<span>${datePipe.transform(row.BuyerPaymentDate)}</span>`;
			},
		},
		{
			field: 'ApprovalState',
			header: 'search-docs.approvalState',
			align: 'center',
			render: (row: DocumentViewModel) => {
				return html`
					<span class="tag ${row.ApprovalState}">${this.translateService.translate(row.ApprovalState, 'list-approvalState')}</span>
				`;
			},
		},
		{
			field: 'PaymentState',
			header: 'search-docs.paymentState',
			align: 'center',
			render: (row: DocumentViewModel) => {
				return html`
					<span class="tag is-payment-state-${row.PaymentState}">
						${this.translateService.translate(row.PaymentState, 'list-paymentState')}
					</span>
				`;
			},
		},
		{
			type: 'actions',
			icon: 'fa-solid fa-ellipsis-v',
			isMenu: true,
			showActions: (row: DocumentViewModel) =>
				!!row.navitationUrl || this.canDeleteDocument(row) || this.canNavigateToFinancialMovements(row),
			menuItems: [
				{
					icon: (row: DocumentViewModel) => row.navitationUrlIcon,
					label: (row: DocumentViewModel) => this.translateService.translate(row.navigationUrlText, 'client'),
					action: (row: DocumentViewModel) => this.navigateToDocument(row.navitationUrl),
					condition: (row: DocumentViewModel) => !!row.navitationUrl,
				},
				{
					icon: 'fa-regular fa-file-invoice-dollar',
					label: this.translateService.translate('search-docs.financialMovements', 'client'),
					action: (row) => this.navigateToFinancialMovements(row),
					condition: (row: DocumentViewModel) => this.canNavigateToFinancialMovements(row),
				},
				{
					icon: 'fa-regular fa-minus-circle',
					label: this.translateService.translate('search-docs.deleteDocument', 'client'),
					action: (row) => this.confirmDocumentDeletion(row),
					condition: (row: DocumentViewModel) => this.canDeleteDocument(row),
				},
			],
		},
	];

	public navigateToFinancialMovements(row: DocumentViewModel) {
		this.router.navigate(['/private/documents/financial-movements', row.Id]);
	}

	public canNavigateToFinancialMovements(row: DocumentViewModel) {
		return (
			!!(
				this.loggedUser?.IsBuyer ||
				this.loggedUser?.IsFinancier ||
				this.loggedUser?.IsSystemAdmin ||
				this.loggedUser?.IsSupportOperator ||
				this.loggedUser?.IsBackOfficeOperator
			) &&
			(row.PaymentState === 'Paid' || row.PaymentState === 'Advanced')
		);
	}

	private initializeBreadcrumbs() {
		this.breadcrumbItems = [
			{
				label: this.translateService.translate('breadcrumb.home', 'client'),
				route: ['/private/home'],
			},
			{
				label: this.translateService.translate('breadcrumb.files-integration', 'client'),
				route: ['/private/files-integration/'],
			},
			{
				label: this.translateService.translate('breadcrumb.files-integration-doc-file', 'client'),
			},
		];
	}

	private initializeTableFilters() {
		this.tableFilters = [
			{
				field: 'Type',
				type: 'select',
				value: null,
				options: this.docTypes.map((type) => ({
					value: type,
					label: this.translateService.translate(type, 'list-docType'),
				})),
				placeholder: this.translateService.translate('search-docs.docType', 'client'),
			},
			{
				field: 'Number',
				type: 'text',
				value: null,
				placeholder: this.translateService.translate('search-docs.docNumber', 'client'),
			},
			{
				field: 'Supplier',
				propertyPath: 'Supplier.Name',
				type: 'select',
				value: null,
				options: this.suppliers.map((company) => ({
					value: company.Id,
					label: `${company.Name} (${company.CountryCode}${company.VatNumber})`,
				})),
				placeholder: this.translateService.translate('search-docs.supplier', 'client'),
			},
			{
				field: 'Buyer',
				propertyPath: 'Contract.Buyer.Name',
				type: 'select',
				value: null,
				options: this.buyers.map((company) => ({
					value: company.Id,
					label: `${company.Name} (${company.CountryCode}${company.VatNumber})`,
				})),
				placeholder: this.translateService.translate('search-docs.buyer', 'client'),
			},
			{
				field: 'BuyerPaymentDate',
				type: 'date-picker',
				value: null,
				placeholder: this.translateService.translate('search-docs.confirmedPaymentDate', 'client'),
				metadata: {
					datePickerConfig: {
						calendars: 2,
						isRange: true,
						format: 'YYYY/MM/DD',
					},
				},
			},
			{
				field: 'CreationDate',
				type: 'date-picker',
				value: [dayjs().subtract(2, 'months').toISOString(), dayjs().toISOString()],
				placeholder: this.translateService.translate('search-docs.creationDate', 'client'),
				metadata: {
					datePickerConfig: {
						calendars: 2,
						isRange: true,
						format: 'YYYY/MM/DD',
					},
				},
			},
			{
				field: 'ApprovalState',
				type: 'select',
				value: null,
				options: this.approvalStates.map((state) => ({
					value: state,
					label: this.translateService.translate(state, 'list-approvalState'),
				})),
				placeholder: this.translateService.translate('search-docs.approvalState', 'client'),
			},
			{
				field: 'PaymentState',
				type: 'select',
				value: null,
				options: this.paymentStates.map((state) => ({
					value: state,
					label: this.translateService.translate(state, 'list-paymentState'),
				})),
				placeholder: this.translateService.translate('search-docs.paymentState', 'client'),
			},
		];
	}

	public onSearchRequested(event: TableSearchEvent): void {
		this.DocumentSearchCriteria = new DocumentSearchCriteria();
		this.DocumentSearchCriteria.FileId = this.fileId;
		this.searchCriteria = this.DocumentSearchCriteria;

		if (this.filterMapper && event.filters?.length > 0) {
			this.filterMapper.mapFiltersToObject(event.filters, this.filterMappings, this.searchCriteria);
		}

		this.tablePagination.pageIndex = event.pageIndex;
		this.tablePagination.pageSize = event.pageSize;

		const pageNumber = event.pageIndex + 1;
		this.performSearch(pageNumber);
	}

	onSelectionChange(selectionEvent: TableSelectionEvent): void {
		if (selectionEvent && typeof selectionEvent === 'object' && 'allSelectedRows' in selectionEvent) {
			this.selectedRows = selectionEvent.allSelectedRows as DocumentViewModel[];
		} else {
			this.selectedRows = selectionEvent as DocumentViewModel[];
		}
	}

	ProcessSearchCriteria(searchCriteriaBase64: string) {
		let searchCriteria = atob(searchCriteriaBase64);

		if (!!searchCriteria) {
			this.queryStringDocumentSearchCriteria = new QueryStringDocumentSearchCriteria(JSON.parse(searchCriteria));
			this.DocumentSearchCriteria.ApprovalStateList = this.queryStringDocumentSearchCriteria.ApprovalStateList;
			this.DocumentSearchCriteria.PaymentStateList = this.queryStringDocumentSearchCriteria.PaymentStateList;
		}
	}

	public performSearch(pageNumber: number) {
		this.isTableLoading = true;
		this.tablePagination.pageIndex = pageNumber - 1;
		this.isPerformingRequest = true;

		const criteria = new DocumentSearchCriteria();
		criteria.OrderBy = 'CreationDate';
		criteria.PageNumber = pageNumber;
		criteria.RowsPerPage = this.tablePagination.pageSize;
		criteria.RestrictionCriteria = this.DocumentSearchCriteria;
		criteria.RestrictionCriteria.FileId = this.fileId;
		criteria.OrderDescending = true;

		this.serverApiService
			.searchDocuments(criteria)
			.pipe(
				finalize(() => {
					this.isResourceLoaded = true;
					this.isPerformingRequest = false;
					this.isTableLoading = false;
					this.isTableReady = true;
					this.cdr.detectChanges();
				})
			)
			.subscribe({
				next: (operationResult: OperationResult<Document[]>) => {
					if (operationResult.IsValid) {
						this.documents = operationResult.Data.map(
							(d) =>
								<DocumentViewModel>{
									Id: d.Id,
									CreationDate: dayjs.utc(d.CreationDate).tz(this.loggedUser?.TimeZone!).format('YYYY/MM/DD HH:mm'),
									ExternalSource: d.ExternalSource,
									ExternalId: d.ExternalId,
									Type: d.Type,
									Number: d.Number,
									TotalAmount: d.TotalAmount,
									Currency: d.Currency,
									IssuedDate: d.IssuedDate,
									DueDate: d.DueDate,
									Buyer: d.Buyer,
									Supplier: d.Supplier,
									BuyerPaymentDate: d.BuyerPaymentDate,
									ApprovalState: d.ApprovalState,
									PaymentDate: d.PaymentDate,
									PaymentState: d.PaymentState,
									FileId: d.FileId,
									RowVersion: d.RowVersion,
									canSelect: this.canDeleteDocument(d),
								}
						);

						let servicesCallsToMake: Array<Observable<OperationResult<DocumentInvolvement[]>> | Observable<OperationResult<number>>> = [];

						let searchDocumentsInvolvementServiceCall = this.serverApiService.searchDocumentsInvolvement(
							this.documents.map(
								(d) =>
									<SearchDocumentInvolvementInput>{
										DocumentId: d.Id,
										DocumentPaymentState: d.PaymentState,
									}
							)
						);

						let countSearchDocumentsServiceCall = this.serverApiService.countSearchDocuments(criteria.RestrictionCriteria);

						servicesCallsToMake.push(searchDocumentsInvolvementServiceCall);
						servicesCallsToMake.push(countSearchDocumentsServiceCall);

						const getAllRequests = forkJoin(servicesCallsToMake);

						getAllRequests.subscribe({
							next: (opResults) => {
								let searchDocumentsInvolvementResult = opResults[0] as OperationResult<DocumentInvolvement[]>;
								let countSearchDocumentsResult = opResults[1] as OperationResult<number>;

								if (!searchDocumentsInvolvementResult.IsValid) {
									let errors = searchDocumentsInvolvementResult.Errors ?? [];

									if (errors && errors.length > 0) {
										this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
											errors: errors,
											autoClose: false,
										});
									}
									return;
								}
								if (!countSearchDocumentsResult.IsValid) {
									let errors = countSearchDocumentsResult.Errors ?? [];

									if (errors && errors.length > 0) {
										this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
											errors: errors,
											autoClose: false,
										});
									}
									return;
								}

								if (searchDocumentsInvolvementResult.IsValid) {
									this.documents = this.documents.map((doc) => {
										let docInvolvement: DocumentInvolvement | undefined = searchDocumentsInvolvementResult.Data.find(
											(d) => d.DocumentId == doc.Id
										);

										if (!!docInvolvement && !!doc) {
											if (docInvolvement.InvolvedEntityType === DocumentInvolvedEntityType.Funding) {
												return {
													...doc,
													navitationUrl: '/private/funding-request/detail/' + docInvolvement.InvolvedEntityId,
													navitationUrlIcon: 'fa-light fa-hand-holding-usd me-1',
													navigationUrlText: 'search-docs.link.funding',
												};
											} else if (docInvolvement.InvolvedEntityType === DocumentInvolvedEntityType.Payment) {
												return {
													...doc,
													navitationUrl: '/private/payment-request/detail/' + docInvolvement.InvolvedEntityId,
													navitationUrlIcon: 'fa-light fa-usd-circle me-1',
													navigationUrlText: 'search-docs.link.payment',
												};
											} else if (docInvolvement.InvolvedEntityType === DocumentInvolvedEntityType.CreditAssignment) {
												return {
													...doc,
													navitationUrl: '/private/credit-assignment/detail/' + docInvolvement.InvolvedEntityId,
													navitationUrlIcon: 'fa-light fa-hands-usd me-1',
													navigationUrlText: 'search-docs.link.creditAssignment',
												};
											}
										}
										return doc;
									});
								}

								if (countSearchDocumentsResult.IsValid) {
									this.tablePagination.totalItems = countSearchDocumentsResult.Data;
									this.tablePagination = { ...this.tablePagination };
								}

								this.cdr.detectChanges();
								this.cdr.markForCheck();

								this.customTable?.triggerFinalRerender();
							},
							error: (httpErrorResponse: HttpErrorResponse) => {
								console.error('error: ' + httpErrorResponse.message);
								this.toasterService.showErrorFromHttpResponse(httpErrorResponse);

								this.customTable?.triggerFinalRerender();
							},
						});
					} else {
						this.toasterService.showError(this.translateService.translate('INVALID_SEARCH_RESULT', 'list-errorCodes'));
					}
				},
				error: (httpErrorResponse: HttpErrorResponse) => {
					console.error('error: ' + httpErrorResponse.message);
					this.toasterService.showErrorFromHttpResponse(httpErrorResponse);

					this.customTable?.triggerFinalRerender();
				},
			});
	}

	private loadListsForFilters(): void {
		this.codeListApiService.getCodeListApprovalState().subscribe({
			next: (operationResult: OperationResult<CodeList<ApprovalStateEnum>>) => {
				if (operationResult.IsValid) {
					this.approvalStates = this.filterApprovalStatesByRole(this.loggedUser, operationResult.Data);
				} else {
					this.toasterService.showError(
						this.translateService.translate('INVALID_RESULT_FROM_SERVER_APPROVALSTATES_CODE_LIST', 'list-errorCodes')
					);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);

				this.customTable?.triggerFinalRerender();
			},
		});

		this.codeListApiService.getCodeListDocumentAdvanceStates().subscribe({
			next: (operationResult: OperationResult<CodeList<DocumentAdvanceStatesEnum>>) => {
				operationResult.IsValid
					? (this.documentAdvancedStates = operationResult.Data.Codes)
					: this.toasterService.showError(
							this.translateService.translate('INVALID_RESULT_FROM_SERVER_DOCUMENT_ADVANCE_STATES_CODE_LIST', 'list-errorCodes')
						);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);

				this.customTable?.triggerFinalRerender();
			},
		});

		this.codeListApiService.getCodeListDocumentTypes().subscribe({
			next: (operationResult: OperationResult<CodeList<DocumentTypesEnum>>) => {
				operationResult.IsValid
					? (this.docTypes = operationResult.Data.Codes)
					: this.toasterService.showError(this.translateService.translate('INVALID_DOCUMENT_TYPES_CODE_LIST', 'list-errorCodes'));
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);

				this.customTable?.triggerFinalRerender();
			},
		});

		this.codeListApiService.getCodeListPaymentStates().subscribe((operationResult: OperationResult<CodeList<PaymentStatesEnum>>) => {
			operationResult.IsValid
				? (this.paymentStates = operationResult.Data.Codes)
				: this.toasterService.showError(
						this.translateService.translate('INVALID_RESULT_FROM_SERVER_PAYMENT_STATE_CODE_LIST', 'list-errorCodes')
					);
		});

		if (
			this.loggedUser?.IsSystemAdmin ||
			this.loggedUser?.IsSupportOperator ||
			this.loggedUser?.IsBackOfficeOperator ||
			this.loggedUser?.IsBuyer
		)
			this.getBuyersAndSuppliersForSystemUserAndBuyer();
		else if (this.loggedUser?.IsSupplier) this.getBuyersAndSuppliersForSupplier();
		else if (this.loggedUser?.IsFinancier) this.getBuyersAndSuppliersForFinancier();
	}

	private getBuyersAndSuppliersForSystemUserAndBuyer(): void {
		this.isPerformingRequest = true;

		this.utilitiesService.fetchBuyersAndSuppliers('all', true).subscribe({
			next: (result) => {
				this.suppliers = result.suppliers;
				this.buyers = result.buyers;

				this.initializeTableFilters();

				this.isPerformingRequest = false;
				this.cdr.detectChanges();
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
				this.isPerformingRequest = false;
			},
		});
	}

	private getBuyersAndSuppliersForSupplier(): void {
		this.isPerformingRequest = true;

		this.utilitiesService.fetchBuyersAndSuppliers('confirming', false).subscribe({
			next: (result) => {
				this.suppliers = result.suppliers;
				this.buyers = result.buyers;

				this.initializeTableFilters();

				this.isPerformingRequest = false;
				this.cdr.detectChanges();
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
				this.isPerformingRequest = false;
			},
		});
	}

	private getBuyersAndSuppliersForFinancier(): void {
		this.isPerformingRequest = true;

		this.utilitiesService.fetchBuyersAndSuppliers('all', true, true).subscribe({
			next: (result) => {
				this.suppliers = result.suppliers;
				this.buyers = result.buyers;

				this.initializeTableFilters();

				this.isPerformingRequest = false;
				this.cdr.detectChanges();
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
				this.isPerformingRequest = false;
			},
		});
	}

	private filterApprovalStatesByRole(loggedUser: User | null, states: CodeList<ApprovalStateEnum>) {
		if (loggedUser?.IsSystemAdmin || loggedUser?.IsSupportOperator || loggedUser?.IsFinancier) {
			return states.Codes;
		} else if (!loggedUser?.IsSystemAdmin && loggedUser?.IsSupplier) {
			return states.Codes.filter((code) => code == ApprovalStateEnum.Approved || code == ApprovalStateEnum.Registered);
		} else {
			return states.Codes.filter((code) => !code.includes(ApprovalStateEnum.Registered));
		}
	}

	public exportDocuments = (): void => {
		const criteria = new DocumentSearchCriteria();
		criteria.OrderBy = 'CreationDate';
		criteria.RowsPerPage = this.tablePagination.pageSize;
		criteria.RestrictionCriteria = this.DocumentSearchCriteria;
		criteria.RestrictionCriteria.FileId = this.fileId;
		criteria.OrderDescending = true;

		this.isPerformingRequest = true;

		this.reportingApiService.exportDocuments(criteria).subscribe({
			next: (operationResult: OperationResult<Report>) => {
				if (operationResult.IsValid && operationResult.Data) {
					this.reportingExportHelperService.processValidReport(operationResult.Data);
				} else {
					this.reportingExportHelperService.processInvalidReport(operationResult, 'Invalid result from server export documents');
				}

				this.isPerformingRequest = false;
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
				this.isPerformingRequest = false;

				this.customTable?.triggerFinalRerender();
			},
			complete: () => {
				this.tableExport = { ...this.tableExport, exporting: false };
				this.cdr.detectChanges();
			},
		});
	};

	public canExportDocumentSearchResults(): boolean {
		return this.tablePagination.totalItems > 0;
	}

	public tableExport: TableExportOption = {
		label: 'search-docs.exportDocuments',
		action: () => this.exportDocuments(),
		condition: () => this.canExportDocumentSearchResults(),
	};

	public gotoDocumentsUploadConfirming(): void {
		this.router.navigate([
			'/private/files-upload/upload/documents/step1',
			DocumentUploadContractTypeExtensions.ToRouteParam(DocumentUploadContractType.Confirming),
		]);
	}

	public gotoDocumentsUploadFactoring(): void {
		this.router.navigate([
			'/private/files-upload/upload/documents/step1',
			DocumentUploadContractTypeExtensions.ToRouteParam(DocumentUploadContractType.Factoring),
		]);
	}

	private GetEtlFileDetails(fileId: string): void {
		this.isPerformingRequest = true;

		this.fileIntegrationApiService.getFileIntegrationById(fileId).subscribe({
			next: (operationResult: OperationResult<FileIntegration>) => {
				if (operationResult.IsValid) {
					this.fileIntegrationDetail = operationResult.Data;
				} else {
					this.toasterService.showError(this.translateService.translate('INVALID_SEARCH_ETL_FILE_ID', 'list-errorCodes'));
				}
				this.isPerformingRequest = false;
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
			},
		});
	}

	public confirmDocumentsDeletions(): void {
		const selectedDocumentCountReplaceString = '[SelectedDocumentCount]';
		const selectedDocuments: DocumentViewModel[] = this.selectedRows;
		const selectedDocumentIds: string[] = selectedDocuments.map((d) => d.Id);
		const selectedDocumentsCount: number = selectedDocuments.length;

		if (selectedDocumentsCount === 0) {
			this.toasterService.showWarning(this.translateService.translate('search-docs.noDocumentsSelected', 'client'));
			return;
		}

		const modalRef = this.modalService.confirm({
			size: 'lg',
			title: this.translateService.translate('search-docs-selectedDocumentsApprovalRevoked.modalConfirmDialogSubmitTitle', 'client'),
			body: {
				content: this.translateService
					.translate('search-docs-selectedDocumentsApprovalRevoked.modalConfirmDialogSubmitContent', 'client')
					.replace(selectedDocumentCountReplaceString, selectedDocumentsCount.toString()),
			},
		});

		modalRef.result
			.then((confirmed) => {
				if (!confirmed) {
					return;
				}

				this.permissionValidatorService.executeIfAllowed(
					(loggedUser: User) => {
						return !loggedUser.IsSupportOperator;
					},
					() => {
						const deleteDocServiceCall = this.serverApiService.deleteDocumentsByIds(selectedDocumentIds);

						this.isPerformingRequest = true;

						deleteDocServiceCall.subscribe({
							next: (operationResult: OperationResult<boolean>) => {
								if (operationResult.IsValid) {
									let sucessMessage = this.translateService
										.translate('search-docs-selectedDocumentApprovalRevoked.sucess', 'client')
										.replace(selectedDocumentCountReplaceString, selectedDocumentsCount.toString());

									this.customTable?.clearSelectionForRows(selectedDocuments);

									this.performSearch(1);
									this.isPerformingRequest = false;

									this.toasterService.showSuccess(sucessMessage);
								} else {
									let errors = this.utilitiesService.GetDistinctOperationErrors(operationResult.Errors);

									if (errors && errors.length > 0) {
										this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
											errors: errors,
											autoClose: false,
										});
									}
									this.isPerformingRequest = false;
								}
							},
							error: (httpErrorResponse: HttpErrorResponse) => {
								this.logger.error('[SearchDocumentsComponent::deleteDocumentsByIds] HttpErrorResponse: ', httpErrorResponse);
								this.toasterService.showError(
									'[SearchDocumentsComponent::deleteDocumentsByIds] HttpErrorResponse: ' +
										httpErrorResponse.error +
										httpErrorResponse.message
								);
								this.isPerformingRequest = false;
							},
						});
					}
				);
			})
			.catch(() => undefined);
	}

	protected canSeeRequestFundingOptions(): boolean {
		if (this.loggedUser?.IsSystemAdmin || this.loggedUser?.IsSupportOperator) {
			return true;
		} else if (this.loggedUser?.IsSupplier && this.loggedUser?.IsCompanyAdmin) {
			return true;
		} else if (this.loggedUser?.IsFinancier && this.loggedUser?.IsCompanyAdmin) {
			return true;
		}

		return false;
	}

	protected gotoConfirmingFundingRequestCreate(): void {
		this.router.navigateByUrl('/private/funding-request/create');
	}

	protected gotoFactoringAdvancePaymentCreate(): void {
		this.router.navigateByUrl('/private/advance-request/create');
	}

	protected canSeeUploadDocumentsOptions(): boolean {
		if (this.loggedUser?.IsSystemAdmin || this.loggedUser?.IsSupportOperator) {
			return true;
		} else if (this.loggedUser?.canUploadDocumentsForConfirming()) {
			return true;
		} else if (this.loggedUser?.canUploadDocumentsForFactoring()) {
			return true;
		}

		return false;
	}

	public navigateToDocument(url: string): void {
		this.router.navigate([url]);
	}

	public confirmDocumentDeletion(document: Document): void {
		const selectedDocumentCountReplaceString = '[SelectedDocumentCount]';
		const documentId: string = document.Id;

		const modalRef = this.modalService.confirm({
			size: 'lg',
			title: this.translateService.translate('search-docs-selectedDocumentApprovalRevoked.modalConfirmDialogSubmitTitle', 'client'),
			body: {
				content: this.translateService
					.translate('search-docs-selectedDocumentApprovalRevoked.modalConfirmDialogSubmitContent', 'client')
					.replace(selectedDocumentCountReplaceString, '1'),
			},
		});

		modalRef.result
			.then(
				(confirmed) => {
					if (!confirmed) {
						return;
					}

					this.permissionValidatorService.executeIfAllowed(
						(loggedUser: User) => {
							return !loggedUser.IsSupportOperator && !loggedUser.IsBackOfficeOperator;
						},
						() => {
							const deleteDocServiceCall = this.serverApiService.deleteDocumentsByIds([documentId]);

							this.isPerformingRequest = true;

							deleteDocServiceCall.subscribe({
								next: (operationResult: OperationResult<boolean>) => {
									if (operationResult.IsValid) {
										let sucessMessage = this.translateService
											.translate('search-docs-selectedDocumentApprovalRevoked.sucess', 'client')
											.replace(selectedDocumentCountReplaceString, '1');

										this.customTable?.clearSelectionForRows([document]);

										this.performSearch(1);
										this.isPerformingRequest = false;

										this.toasterService.showSuccess(sucessMessage);
									} else {
										let errors = this.utilitiesService.GetDistinctOperationErrors(operationResult.Errors);

										if (errors && errors.length > 0) {
											this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
												errors: errors,
												autoClose: false,
											});
										}
										this.isPerformingRequest = false;
									}
								},
								error: (httpErrorResponse: HttpErrorResponse) => {
									this.logger.error('[SearchDocumentsComponent::deleteDocumentsByIds] HttpErrorResponse: ', httpErrorResponse);

									this.toasterService.showError(
										'[SearchDocumentsComponent::deleteDocumentsByIds] HttpErrorResponse: ' +
											httpErrorResponse.error +
											httpErrorResponse.message
									);
									this.isPerformingRequest = false;
									this.isTableLoading = false;
								},
							});
						}
					);
				},
				() => {}
			)
			.catch((error) => console.error('Error from modal' + error));
	}

	public canDeleteDocument(document?: Document): boolean {
		if (
			((this.loggedUser?.IsFinancier || this.loggedUser?.IsBuyer) && this.loggedUser?.IsCompanyAdmin) ||
			this.loggedUser?.IsSystemAdmin ||
			this.loggedUser?.IsSupportOperator
		) {
			return document
				? document.PaymentState === DocumentPaymentState.Unpaid && document.ApprovalState === ApprovalStateEnum.Approved
				: true;
		}
		return false;
	}

	public showModal(event?: MouseEvent): void {
		if (event) {
			event.stopPropagation();
		}
		this.isModalVisible = true;
	}

	public closeModal(): void {
		this.isModalVisible = false;
		// Clear all inputs when closing modal
		this.companyId = '';
		this.fileRelatedContractId = '';
		this.modalDateRange = [];
		this.isDateRangeValid = false;
		this.contractsInCompanyValidation = null;
	}

	public onModalRangeChange(event: string[]): void {
		this.modalDateRange = event;
		// Update validation state based on date range
		this.isDateRangeValid = event && event.length >= 2 && !!event[0] && !!event[1];
	}

	public onDateRangeValidationChange(validation: { isValid: boolean; errors: string[] }): void {
		this.isDateRangeValid = validation.isValid;
	}

	public onCompanyChange(companyId: string): void {
		this.companyId = companyId;
	}

	public onContractChange(contractId: string): void {
		this.fileRelatedContractId = contractId;
	}

	public onValidationChange(validation: ContractsInCompanyValidation): void {
		this.contractsInCompanyValidation = validation;
	}

	public exportFinancialMovements(): void {
		const criteria: ReportingFinancialMovementsCriteria = {
			contractId: this.fileRelatedContractId,
			movementValueDateStart: this.modalDateRange[0],
			movementValueDateEnd: this.modalDateRange[1],
		};

		this.isExportingRequest = true;
		this.reportingApiService.reportingFinancialMovements(criteria).subscribe({
			next: (result) => {
				if (result.IsValid && result.Data) {
					this.reportingExportHelperService.processValidReport(result.Data);
				} else {
					this.reportingExportHelperService.processInvalidReport(result, 'Invalid result from server export financial movements');
				}

				this.isExportingRequest = false;
			},
			error: (error) => {
				this.logger.error('[SearchDocumentsComponent::exportFinancialMovements] Error: ', error);
				this.toasterService.showError(
					this.translateService.translate('search-documents.error-exporting-financial-movements-please-try-again', 'client')
				);
				this.isExportingRequest = false;
			},
		});
	}

	public canSeeExportFinancialMovementsOptions(): boolean {
		return (
			!!this.loggedUser &&
			(this.loggedUser.IsSystemAdmin ||
				this.loggedUser.IsFinancier ||
				this.loggedUser.IsConfirmingBuyer ||
				this.loggedUser.IsSupportOperator ||
				this.loggedUser.IsBackOfficeOperator)
		);
	}

	public getFinancialMovementsDateRangeValidationRules(): ValidationRule[] {
		const datePickerRules = CustomDatePickerComponent.createRules(this.translateService);
		return [datePickerRules.required()];
	}

	public isExportFinancialMovementsDisabled(): boolean {
		// Check if company is valid
		const isCompanyValid = this.contractsInCompanyValidation?.isCompanyValid ?? false;

		// Check if contract is valid
		const isContractValid = this.contractsInCompanyValidation?.isContractValid ?? false;

		// Check if date range is valid
		const isDateValid = this.isDateRangeValid;

		// Disable if any required field is invalid or if export is in progress
		return !isCompanyValid || !isContractValid || !isDateValid || this.isExportingRequest;
	}

	public canSeeUploadDocumentsButton(): boolean {
		return (
			!!this.loggedUser &&
			!this.fileIntegrationDetail &&
			this.canSeeUploadDocumentsOptions() &&
			this.loggedUser.canUploadDocumentsForConfirming() &&
			this.loggedUser.canUploadDocumentsForFactoring()
		);
	}

	public uploadDocumentsMenuItems: MenuItem[] = [
		{
			text: 'search-docs.documentRecordsUploadConfirming',
			action: () => this.gotoDocumentsUploadConfirming(),
		},
		{
			text: 'search-docs.documentRecordsUploadFactoring',
			action: () => this.gotoDocumentsUploadFactoring(),
		},
	];

	public canSeeDocumentRecordsUploadConfirmingButton(): boolean {
		return (
			!!this.loggedUser &&
			!this.fileIntegrationDetail &&
			this.canSeeUploadDocumentsOptions() &&
			this.loggedUser.canUploadDocumentsForConfirming() &&
			!this.loggedUser.canUploadDocumentsForFactoring()
		);
	}

	public canSeeDocumentRecordsUploadFactoringButton(): boolean {
		return (
			!!this.loggedUser &&
			!this.fileIntegrationDetail &&
			this.canSeeUploadDocumentsOptions() &&
			!this.loggedUser.canUploadDocumentsForConfirming() &&
			this.loggedUser.canUploadDocumentsForFactoring()
		);
	}

	public canSeeRequestFundingButton(): boolean {
		return (
			!!this.loggedUser &&
			!this.loggedUser.IsBackOfficeOperator &&
			this.canSeeRequestFundingOptions() &&
			this.loggedUser.canRequestConfirmingFunding() &&
			this.loggedUser.canRequestFactoringAdvances()
		);
	}

	public requestFundingMenuItems: MenuItem[] = [
		{
			text: 'search-docs.newFundingRequest',
			action: () => this.router.navigateByUrl('/private/funding-request/create'),
		},
		{
			text: 'search-docs.newAdvanceRequest',
			action: () => this.router.navigateByUrl('/private/advance-request/create'),
		},
	];

	public canSeeConfirmingOnlyRequestFundingButton(): boolean {
		return (
			!!this.loggedUser &&
			this.canSeeRequestFundingOptions() &&
			this.loggedUser.canRequestConfirmingFunding() &&
			!this.loggedUser.canRequestFactoringAdvances()
		);
	}

	public canSeeFactoringOnlyRequestAdvancePaymentButton(): boolean {
		return (
			!!this.loggedUser &&
			this.canSeeRequestFundingOptions() &&
			!this.loggedUser.canRequestConfirmingFunding() &&
			this.loggedUser.canRequestFactoringAdvances()
		);
	}

	public getRowId(row: DocumentViewModel): string {
		return row.Id + row.ContractId + row.FileId;
	}
}
